import {
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerClusterer,
  Circle,
  Polyline,
  useLoadScript, // useJsApiLoader,
} from '@react-google-maps/api';
import { CommImageNameComp } from 'custom_modules/Members/CommonComponents';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import call from '../../assets/img/icons/call.svg';
import { CommonFlex, CommonText } from '../../styledComponents/common';
import { getSpiderfiedCenters, spiderfyMarkers } from '../../utils/spiderfyMarkers';
import './styles.css';

const MarkerInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px;
  border-radius: 10px;
  background-color: #ffffff;
  /* width: 200px; */
  height: max-content;
  gap: 10px;
  margin: 10px;
  max-height: 320px;
  overflow-y: auto;
`;

const ImageAndNameContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const PhoneNumberContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0;
`;

const LastSeenContainer = styled.div`
  display: flex;
  align-items: center;
`;

const options = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  disableDoubleClickZoom: true,
  gestureHandling: 'cooperative',
  mapId: process.env.REACT_APP_JOB_SITES_MAP_ID,
};

const containerStyle = {
  width: '100%',
  height: '85vh',
  borderLeft: '1px solid #e4e7eb',
  borderRadius: '10px',
};

const center = {
  lat: 21.424090674479555,
  lng: 39.822141261515604,
};

const circleOptions = {
  strokeColor: '#6F7AFF',
  strokeWeight: 2,
  fillColor: '#6F7AFF',
  fillOpacity: 0.2,
};

const userMarkerFallbackColors = [
  '#F97316',
  '#2563EB',
  '#16A34A',
  '#DC2626',
  '#7C3AED',
  '#D97706',
];

const getUserMarkerColor = user => {
  if (user?.color) {
    return user.color;
  }

  const source = `${user?.employee?.id || ''}${user?.employee?.first_name || ''}${user?.employee?.last_name || ''}`;
  const hash = source.split('').reduce((accumulator, character) => {
    return accumulator + character.charCodeAt(0);
  }, 0);

  return userMarkerFallbackColors[hash % userMarkerFallbackColors.length];
};

const createUserMarkerIcon = user => {
  const firstName = user?.employee?.first_name || '';
  const lastName = user?.employee?.last_name || '';
  const initials = lastName
    ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    : firstName.substring(0, 2).toUpperCase();
  const markerId = user?.employee?.id || `${firstName}-${lastName}` || 'user';
  const markerColor = getUserMarkerColor(user);
  const avatarUrl = user?.employee?.avatar;

  const svgString = avatarUrl
    ? `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <defs>
          <clipPath id="avatarClip-${markerId}">
            <circle cx="20" cy="20" r="15" />
          </clipPath>
        </defs>
        <circle cx="20" cy="20" r="16.5" fill="${markerColor}" opacity="0.22">
          <animate attributeName="opacity" values="0.15;0.7;0.15" dur="1.4s" repeatCount="indefinite" />
          <animate attributeName="r" values="16.5;19;16.5" dur="1.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="20" cy="20" r="15.5" fill="#FFFFFF" stroke="${markerColor}" stroke-width="1" />
        <image
          href="${avatarUrl}"
          x="5"
          y="5"
          width="30"
          height="30"
          preserveAspectRatio="xMidYMid slice"
          clip-path="url(#avatarClip-${markerId})"
        />
      </svg>
    `
    : `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="16.5" fill="${markerColor}" opacity="0.22">
          <animate attributeName="opacity" values="0.15;0.7;0.15" dur="1.4s" repeatCount="indefinite" />
          <animate attributeName="r" values="16.5;19;16.5" dur="1.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="20" cy="20" r="15.5" fill="${markerColor}" stroke="#FFFFFF" stroke-width="1" />
        <text x="20" y="21" font-family="'Mona Sans', sans-serif" font-size="13" font-weight="700" fill="#FFFFFF" text-anchor="middle" dominant-baseline="middle">${initials || 'U'}</text>
      </svg>
    `;

  return `data:image/svg+xml;base64,${btoa(svgString)}`;
};

function Map({ userList, isAwayUsers, userCurrentLocation }) {
  const { t } = useTranslation();

  const [markers, setMarkers] = useState([]);
  const [spiderfiedMarkers, setSpiderfiedMarkers] = useState([]);
  const [spiderfiedCenters, setSpiderfiedCenters] = useState([]);
  const [activeMarkerIds, setActiveMarkerIds] = useState([]);

  const [maxZoom, setMaxZoom] = useState(22);

  const { isLoaded, loadError } = useLoadScript({
    id: 'live-location-map',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    mapIds: [process.env.REACT_APP_JOB_SITES_MAP_ID],
  });

  const [map, setMap] = useState(null);

  useEffect(() => {
    if (map && userCurrentLocation) {
      map.panTo(userCurrentLocation);
      map.setZoom(11);
    }
  }, [map, userCurrentLocation]);

  useEffect(() => {
    if (map) {
      map.addListener('zoom_changed', () => {
        setActiveMarkerIds([]);
      });
    }
  }, [map]);
  useEffect(() => {
    setActiveMarkerIds([]);
  }, [isAwayUsers]);

  useEffect(() => {
    if (map && spiderfiedMarkers.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      spiderfiedMarkers.forEach(marker => {
        if (
          marker.location &&
          isFinite(marker.location.lat) &&
          isFinite(marker.location.lng)
        ) {
          bounds.extend(marker.location);
        }
      });
      map.fitBounds(bounds);
    }
  }, [map, spiderfiedMarkers]);

  useEffect(() => {
    // populate markers
    const markers = userList.map(user => {
      const hasAvatar = !!user?.employee?.avatar;
      const marker = {
        id: user?.employee?.id,
        location: {
          lat: parseFloat(user?.latest_location?.lat),
          lng: parseFloat(user?.latest_location?.long),
        },
        icon: createUserMarkerIcon(user),
        avatarUrl: hasAvatar ? user.employee.avatar : null,
        name: `${user?.employee?.first_name} ${user?.employee?.last_name}`,
        phone: user?.employee?.phone,
        office: user?.employee?.office,
        lastSeen: (user?.latest_location?.timestamp ?? 0) * 1000,
        color: user?.color,
      };
      return marker;
    });
    setMarkers(markers);
  }, [userList]);

  useEffect(() => {
    const spiderfied = spiderfyMarkers(markers);
    setSpiderfiedMarkers(spiderfied);
    setSpiderfiedCenters(getSpiderfiedCenters(spiderfied));
  }, [markers]);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  const handleMarkerClick = (markerId, markerLocation) => {
    const sameLocationMarkers = markers.filter(
      marker =>
        marker.location.lat === markerLocation.lat &&
        marker.location.lng === markerLocation.lng,
    );
    if (map?.getZoom() === maxZoom) {
      setActiveMarkerIds(sameLocationMarkers.map(marker => marker.id));
    } else if (markerId) {
      setActiveMarkerIds([markerId]);
    }
  };

  const handleClusterClick = cluster => {
    if (map?.getZoom() < maxZoom) {
      map.panTo(cluster.getCenter());
      return;
    }

    const clickedMarkers = cluster.getMarkers(); // Retrieves Google Maps Marker objects
    const includedMarkers = [];
    const markerDetails = clickedMarkers.map(clickedMarker => {
      const position = clickedMarker.getPosition(); // Get the marker's position (lat/lng)
      const lat = position.lat();
      const lng = position.lng();
      const marker = markers.find(
        marker =>
          marker.location.lat === lat &&
          marker.location.lng === lng &&
          !includedMarkers.includes(marker.id),
      );
      includedMarkers.push(marker.id);
      return marker;
    });

    setActiveMarkerIds(markerDetails.map(marker => marker.id));
  };

  if (loadError) {
    return <div>Map cannot be loaded right now! Please try again later</div>;
  }
  if (!isLoaded) return null;

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      options={{
        ...options,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.LEFT_BOTTOM,
        },
      }}
      center={center}
      zoom={8}
      onLoad={map => setMap(map)}
      onUnmount={onUnmount}
    >
      {spiderfiedMarkers
        .filter(marker => marker.originalLocation)
        .map(marker => (
          <Polyline
            key={`line-${marker.id}`}
            path={[marker.originalLocation, marker.location]}
            options={{
              strokeColor: '#2266EF',
              strokeOpacity: 0.6,
              strokeWeight: 2,
              geodesic: false,
            }}
          />
        ))}

      {spiderfiedCenters.map((center, index) => (
        <Circle
          key={`center-${index}`}
          center={center}
          radius={8}
          options={{
            fillColor: '#2266EF',
            fillOpacity: 0.4,
            strokeColor: '#2266EF',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            clickable: false,
          }}
        />
      ))}

      <MarkerClusterer
        options={{
          averageCenter: true,
          minimumClusterSize: 5,
          imagePath:
            'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
          zoomOnClick: true,
          gridSize: 75,
          imageExtension: 'png',
          imageSizes: [53, 56, 66, 78, 90],
        }}
        onClick={handleClusterClick}
      >
        {clusterer =>
          spiderfiedMarkers.map((marker, index) => {
            return (
              <Marker
                key={marker.id}
                icon={marker.icon}
                position={marker.location}
                radius="16px"
                clusterer={clusterer}
                onClick={() => handleMarkerClick(marker.id, marker.location)}
              >
                {activeMarkerIds.includes(marker.id) &&
                  activeMarkerIds[0] === marker.id && (
                    <InfoWindow
                      position={marker.location}
                      onCloseClick={() => setActiveMarkerIds([])}
                    >
                      <div
                        style={{
                          paddingTop: '20px',
                          paddingBottom: '10px',
                        }}
                      >
                        <MarkerInfoContainer>
                          {activeMarkerIds.length > 1 ? (
                            activeMarkerIds.map((id, idx) => {
                              const activeMarker = markers.find(
                                marker => marker.id === id,
                              );
                              return (
                                <div key={activeMarker.id}>
                                  <CommImageNameComp
                                    imgSource={activeMarker.avatarUrl}
                                    name={activeMarker.name}
                                    size="36px"
                                    fontSize="13px"
                                    index={index}
                                    color={activeMarker.color}
                                  />
                                  {activeMarker.office && (
                                    <CommonFlex gap="12px">
                                      <CommonText $label>
                                        {t('team')}
                                      </CommonText>
                                      <CommonText name>
                                        {activeMarker.office}
                                      </CommonText>
                                    </CommonFlex>
                                  )}
                                  {activeMarker.phone &&
                                    activeMarker.phone !== 'null' && (
                                      <PhoneNumberContainer>
                                        <img
                                          src={call}
                                          alt="call"
                                          style={{
                                            width: '20px',
                                            height: '20px',
                                            marginRight: '10px',
                                          }}
                                        />
                                        <div>
                                          {activeMarker.phone || 'Not found'}
                                        </div>
                                      </PhoneNumberContainer>
                                    )}
                                  {isAwayUsers && (
                                    <LastSeenContainer>
                                      <div>Last seen: </div>
                                      <div style={{ marginLeft: '5px' }}>
                                        {moment(activeMarker.lastSeen).format(
                                          'Do MMM, HH:mm',
                                        )}
                                      </div>
                                    </LastSeenContainer>
                                  )}
                                  {idx < activeMarkerIds.length - 1 && (
                                    <hr
                                      style={{
                                        width: '100%',
                                        marginBottom: '2px',
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div>
                              <CommImageNameComp
                                imgSource={marker.avatarUrl}
                                name={marker.name}
                                size="36px"
                                fontSize="13px"
                                index={index}
                                color={marker.color}
                                designation={marker.designation}
                                showAddDesignation={false}
                              />
                              {marker.office && (
                                <CommonFlex gap="12px">
                                  <CommonText $label>{t('team')}</CommonText>
                                  <CommonText name>{marker.office}</CommonText>
                                </CommonFlex>
                              )}
                              {marker.phone && marker.phone !== 'null' && (
                                <PhoneNumberContainer>
                                  <img
                                    src={call}
                                    alt="call"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      marginRight: '10px',
                                    }}
                                  />
                                  <div>{marker.phone || 'Not found'}</div>
                                </PhoneNumberContainer>
                              )}
                              {isAwayUsers && (
                                <LastSeenContainer>
                                  <div>Last seen: </div>
                                  <div style={{ marginLeft: '5px' }}>
                                    {moment(marker.lastSeen).format(
                                      'Do MMM, HH:mm',
                                    )}
                                  </div>
                                </LastSeenContainer>
                              )}
                            </div>
                          )}
                        </MarkerInfoContainer>
                      </div>
                    </InfoWindow>
                  )}
              </Marker>
            );
          })
        }
      </MarkerClusterer>
    </GoogleMap>
  );
}

export default Map;
